import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../../Redux/store";
import { getValue } from "../../../../utils/object.utils";
import { TableFormV1 } from "../../../Table/TableForm/TableForm";
import { BtnSize, BtnType, Button } from "../../../Button";
import { StudentResultTitle } from "../../../../Constants/tableTitles/TableFormTitle/studentResult.tableForm.title";
import {
  setMetaData,
  setRemarks,
  updateGradeInResultEntry,
  updateMarksInResultEntry,
} from "../../../../Redux/Slices/studentResuilt.slice";
import {
  useDownloadStudentResult,
  useSaveStudentResults,
} from "../../../../hooks/resultsHooks/studentResult.hooks";
import { GridCellParams } from "@mui/x-data-grid";
import { SUBJECT_TYPE } from "../../../../Constants/Enums/Subject.enums";
import {
  setErrorMessage,
  setSuccessMessage,
} from "../../../../Redux/Slices/alert.slice";
import { parseApiError } from "../../../../Helper/apiError.helper";
import { SearchInput } from "../../../input/searchInput";
import {
  ALIGN_ITEMS,
  DISPLAY,
  FLEX_DIRECTION,
  JUSTIFY_CONTENT,
} from "../../../../Constants/Enums/styles.enum";
import { GridWrapper } from "../../../GridWrapper/GridWrapper";
import { useMobile } from "../../../../hooks/deviceHooks/useDevice.hooks";
import { StudentResultReport } from "../StudentResultReport";

interface Props {
  resultId: number;
}
export const StudentResultform = ({ resultId }: Props) => {
  const dispatch = useDispatch();

  const isMobile = useMobile();

  const { selectedStudent } = useSelector((state: RootState) => ({
    selectedStudent: state.studentResult.selectedStudentForResult,
  }));

  const metadata = getValue(selectedStudent, "metadata", {});

  const link = getValue(selectedStudent, "link", null);

  const { isLoading: isSavingResults, mutate: saveStudentResult } =
    useSaveStudentResults({
      onSuccess: () => {
        dispatch(setSuccessMessage("Result saved successfully"));
      },
      onError: (error: Error) => {
        dispatch(setErrorMessage(parseApiError(error)));
      },
    });

  const {
    isLoading: isStudentResultDownloading,
    mutate: downloadStidemtResult,
  } = useDownloadStudentResult({
    onSuccess: (studentResult) => {
      if (studentResult) {
        window.open(studentResult, "_blank");
      }
    },
    onError: (error: Error) => {
      dispatch(setErrorMessage(parseApiError(error)));
    },
  });

  if (!selectedStudent) {
    return <></>;
  }

  const resultEntries = getValue(selectedStudent, "result_entries", []);

  const transformedResultEntry = resultEntries
    .map((entry: any) => ({
      ...entry,
      id: entry?.subject_id,
    }))
    .filter((entry: any) => !!entry);

  const handleCellUpdate = (updateCell: any) => {
    const subjectId = getValue(updateCell, "subject_id");
    const marks = getValue(updateCell, "marks");
    const grade = getValue(updateCell, "grade");
    const changedField = getValue(updateCell, "changedField");

    if (changedField === "marks") {
      dispatch(updateMarksInResultEntry({ subjectId, value: marks }));
    }

    if (changedField === "grade") {
      dispatch(updateGradeInResultEntry({ subjectId, value: grade }));
    }
  };

  const handleSaveStudentResult = () => {
    const student_id = getValue(selectedStudent, "student_id");
    const result_entries = getValue(selectedStudent, "result_entries", []);

    const transformedResultEntries = result_entries.map((entry: any) => ({
      subject_id: entry?.subject_id,
      marks: entry?.marks,
      grade: entry?.grade,
    }));

    const metadata = getValue(selectedStudent, "metadata", {});
    saveStudentResult({
      student_id,
      result_id: resultId,
      result_entries: transformedResultEntries,
      remarks: selectedStudent?.remarks,
      height: metadata?.height,
      weight: metadata?.weight,
      attendance: metadata?.attendance,
    });
  };

  const handleStudentResult = () => {
    const studentId = getValue(selectedStudent, "student_id");

    downloadStidemtResult({
      studentId,
      resultId,
    });
  };

  const validations = [
    {
      marks: {
        validationFn: (validationFn: any) => {
          const fullMarks = getValue(validationFn, "full_marks", 0);
          const marks = getValue(validationFn, "marks", 0);

          return {
            isValid: !!marks && fullMarks >= marks && marks >= 0,
            errorMessage: "Marks can't be greater than full marks",
          };
        },
      },
    },
  ];

  const isCellEditable = (params: GridCellParams) => {
    const subjectType = getValue(params, "row.subject_type");
    const field = getValue(params, "field");

    if (field === "grade") {
      return subjectType === SUBJECT_TYPE.EXTRA_CURRICULAR;
    }
    if (field === "marks") {
      return subjectType !== SUBJECT_TYPE.EXTRA_CURRICULAR;
    }

    return true;
  };

  const studentName = getValue(selectedStudent, "first_name", "");

  return (
    <div
      style={{
        display: DISPLAY.FLEX,
        flexDirection: FLEX_DIRECTION.COLUMN,
        gap: "50px",
      }}
    >
      <StudentResultReport reportLink={link} studentName={studentName} />

      <div
        className="overflow-y-scroll"
        style={{
          height: "380px",
        }}
      >
        <TableFormV1
          columns={StudentResultTitle}
          initialRow={transformedResultEntry}
          onCellChange={handleCellUpdate}
          validations={validations}
          isCellEditable={isCellEditable}
        />
      </div>
      <GridWrapper
        totalColumns={6}
        childrens={[
          {
            component: (
              <div
                style={{
                  display: DISPLAY.FLEX,
                  flexDirection: FLEX_DIRECTION.COLUMN,
                  flex: 1,
                  gap: 10,
                }}
              >
                <div
                  style={{
                    display: DISPLAY.FLEX,
                    flexDirection: FLEX_DIRECTION.ROW,
                    justifyContent: JUSTIFY_CONTENT.SPACE_BETWEEN,
                    gap: 25,
                    flex: 1,
                  }}
                >
                  <SearchInput
                    label={"Attendance"}
                    placeHolder="Enter attendance"
                    value={metadata?.attendance}
                    setValue={(value) => {
                      dispatch(
                        setMetaData({
                          ...metadata,
                          attendance: +value,
                        }),
                      );
                    }}
                  />
                  <SearchInput
                    label={"Height"}
                    placeHolder="Enter height"
                    value={metadata?.height}
                    setValue={(value) => {
                      dispatch(
                        setMetaData({
                          ...metadata,
                          height: +value,
                        }),
                      );
                    }}
                    multiline
                  />
                  <SearchInput
                    label={"Weight"}
                    placeHolder="Enter weight"
                    value={metadata?.weight}
                    setValue={(value) => {
                      dispatch(
                        setMetaData({
                          ...metadata,
                          weight: +value,
                        }),
                      );
                    }}
                    multiline
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <SearchInput
                    label={"Remarks"}
                    placeHolder="Enter remarks"
                    value={selectedStudent.remarks}
                    setValue={(value) => {
                      dispatch(setRemarks(value as string));
                    }}
                    multiline
                  />
                </div>
              </div>
            ),
            gridSpan: 5,
          },
          {
            component: (
              <div
                style={{
                  display: DISPLAY.FLEX,
                  flexDirection: !isMobile
                    ? FLEX_DIRECTION.COLUMN
                    : FLEX_DIRECTION.ROW,
                  gap: 10,
                }}
              >
                <Button
                  text="Save"
                  btnType={BtnType.primary}
                  btnSize={BtnSize.md}
                  loading={isSavingResults}
                  onClick={handleSaveStudentResult}
                />
                <Button
                  text="Export result"
                  btnType={BtnType.basic}
                  btnSize={BtnSize.md}
                  loading={isStudentResultDownloading}
                  onClick={handleStudentResult}
                />
              </div>
            ),
            gridSpan: 1,
            style: {
              display: DISPLAY.FLEX,
              alignItems: ALIGN_ITEMS.CENTER,
            },
          },
        ]}
      />
    </div>
  );
};
